import {Applicant, type BankAccount} from '~/src/models/applicant'
import {ContactInfo, JobDetail, DirectDeposit, SavedFilter} from '../hooks/useCandidateState'
import type {CandidateAssignment, DashboardData} from '../components/candidate/dashboard/types'
import type {
  CandidateProfileData,
  CandidateJobData,
  EmergencyContact,
  PersonalDetails,
  PersonalDetailsUpdate,
} from '../components/candidate/profile/types'
import type {
  CandidateCreateTimeOffRequestInput,
  CandidateTimeOffProjection,
  CandidateTimeOffProjectionInput,
  CandidateTimeOffRequestApiResponse,
  CandidateTimeOffRequest,
  CandidateTimeOffSummaryApiResponse,
  CandidateTimeOffSummary,
} from '../components/candidate/profile/time-off/types'
import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'
import type {PaystubResponse} from './paystubsApi'
import type {TaxInfoResponse} from './applicantTaxApi'
import type {TimecardResponse} from './payrollApi'

// API service for candidate-related operations
// Dashboard and assignments use real backend endpoints; other methods use mocks

export interface CandidateApiResponse<T> {
  data: T
  success: boolean
  message?: string
}

const mapCandidateTimeOffRequest = (
  request: CandidateTimeOffRequestApiResponse,
): CandidateTimeOffRequest => ({
  id: request.id,
  assignmentId: request.assignmentId,
  policyId: request.policyId,
  categoryName: request.typeName,
  type: 'Request',
  status: request.status,
  startDate: request.startDateIso,
  endDate: request.endDateIso,
  dailyHours: request.dailyHours,
  totalHours: request.totalHours,
  submittedAt: request.createdDate,
  note: request.note,
  denialReason: request.denialReason,
  externalRequestId: request.externalRequestId ?? null,
})

export interface SubmitComplianceFormsRequest {
  ssn: string
  formType: 'I9' | 'W4' | 'DirectDeposit' | 'EmergencyContact' | 'BackgroundCheck'
  formData: Record<string, unknown>
  submittedBy?: string
}

export interface CallRecord {
  _id: string
  applicantId: string
  contactName: string
  phoneNumber: string
  callType: 'incoming' | 'outgoing' | 'missed'
  duration: number
  startTime: Date
  endTime: Date
  notes?: string
  createdBy: string
}

export interface TimesheetEntry {
  id: string
  employeeId: string
  weekEnding: Date
  regularHours: number
  overtimeHours: number
  doubletimeHours: number
  ptoHours: number
  status: 'pending' | 'approved' | 'submitted'
}

export interface TaxSetup {
  id: string
  employeeId: string
  jurisdiction: string
  filingStatus: string
  exemptions: number
  additionalWithholding: string
  exempt: boolean
}

/**
 * Safe subset of the applicant returned by the SSN-lookup endpoint.
 *
 * SECURITY: The BE response also contains `ssn` (the full decrypted SSN).
 * This interface deliberately OMITS that field. The service method
 * destructures it out before returning so the raw SSN never reaches
 * component state. See `CandidateApiService.getApplicantBySSN`.
 */
export interface ApplicantSSNLookupResult {
  id: string
  firstName: string
  lastName: string
  last4ssn: string
  employeeID?: string
  agencyId: string
}

export interface CandidatePaychecksListData {
  paychecks: PaystubResponse[]
  total: number
}

export interface CandidateDocumentItem {
  fileId: string
  documentId: string
  folderName: string
  name: string
  extension: string
  type: string
  uploadedBy: string | null
  uploadedAt: string | null
  status: string
  canDownload: boolean
  /**
   * STFFLG-4284: set on an eSign request the candidate has signed while another recipient
   * has not (status 'Signed', canDownload false). Absent on every other row. Optional
   * because the backend deploys separately.
   */
  signatureState?: 'awaiting_others'
}

export interface CandidateDocumentsListData {
  documents: CandidateDocumentItem[]
}

export class CandidateApiService extends BaseApi {
  // Assignment History
  async getContactInfo(candidateId: string): Promise<CandidateApiResponse<ContactInfo>> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500))

    const mockContactInfo: ContactInfo = {
      homeEmail: 'joe_buttons@email.com',
      workPhone: '952 452 4625',
      employeeNumber: '10',
    }

    return {
      data: mockContactInfo,
      success: true,
      message: 'Contact information retrieved successfully',
    }
  }

  async updateContactInfo(
    candidateId: string,
    contactInfo: Partial<ContactInfo>,
  ): Promise<CandidateApiResponse<ContactInfo>> {
    await new Promise((resolve) => setTimeout(resolve, 800))

    // Mock update logic
    const updated: ContactInfo = {
      homeEmail: 'joe_buttons@email.com',
      workPhone: '952 452 4625',
      employeeNumber: '10',
      ...contactInfo,
    }

    return {
      data: updated,
      success: true,
      message: 'Contact information updated successfully',
    }
  }

  async getJobDetails(candidateId: string): Promise<CandidateApiResponse<JobDetail[]>> {
    await new Promise((resolve) => setTimeout(resolve, 600))

    const mockJobDetails: JobDetail[] = [
      {
        effectiveDate: '07/22/2025 - 07/25/2025',
        jobTitle: 'Forklift Driver',
        assignmentId: '7473995',
        departmentSite: 'Teg Broadway - Floor',
        status: 'Active',
        compensation: '$18 /hrs',
        otRate: '$25 /hrs',
      },
      {
        effectiveDate: '07/22/2025 - 07/25/2025',
        jobTitle: 'Cashier',
        assignmentId: '7473995',
        departmentSite: 'Teg Broadway - Floor',
        status: 'Terminated',
        compensation: '$18 /hrs',
        otRate: '$25 /hrs',
      },
    ]

    return {
      data: mockJobDetails,
      success: true,
      message: 'Job details retrieved successfully',
    }
  }

  async updateJobDetail(
    candidateId: string,
    jobDetailId: string,
    jobDetail: Partial<JobDetail>,
  ): Promise<CandidateApiResponse<JobDetail>> {
    await new Promise((resolve) => setTimeout(resolve, 900))

    const updated: JobDetail = {
      effectiveDate: '07/22/2025 - 07/25/2025',
      jobTitle: 'Forklift Driver',
      assignmentId: '7473995',
      departmentSite: 'Teg Broadway - Floor',
      status: 'Active',
      compensation: '$18 /hrs',
      otRate: '$25 /hrs',
      ...jobDetail,
    }

    return {
      data: updated,
      success: true,
      message: 'Job detail updated successfully',
    }
  }

  async getDirectDepositInfo(candidateId: string): Promise<CandidateApiResponse<DirectDeposit[]>> {
    await new Promise((resolve) => setTimeout(resolve, 550))

    const mockDirectDeposit: DirectDeposit[] = [
      {
        bankName: 'Bank of America',
        accountType: 'Checking Account',
        routingNumber: 'xxxxxx123',
        accountNumber: 'xxxxxxxxxxxxx123',
        percentage: '100%',
        amount: '$1440',
      },
    ]

    return {
      data: mockDirectDeposit,
      success: true,
      message: 'Direct deposit information retrieved successfully',
    }
  }

  async updateDirectDepositInfo(
    candidateId: string,
    directDeposit: Partial<DirectDeposit>,
  ): Promise<CandidateApiResponse<DirectDeposit>> {
    await new Promise((resolve) => setTimeout(resolve, 1000))

    const updated: DirectDeposit = {
      bankName: 'Bank of America',
      accountType: 'Checking Account',
      routingNumber: 'xxxxxx123',
      accountNumber: 'xxxxxxxxxxxxx123',
      percentage: '100%',
      amount: '$1440',
      ...directDeposit,
    }

    return {
      data: updated,
      success: true,
      message: 'Direct deposit information updated successfully',
    }
  }

  // Call Records
  async getCallHistory(candidateId: string): Promise<CandidateApiResponse<CallRecord[]>> {
    await new Promise((resolve) => setTimeout(resolve, 400))

    const mockCallHistory: CallRecord[] = [
      {
        _id: '1',
        applicantId: candidateId,
        contactName: 'John Smith',
        phoneNumber: '555-123-4567',
        callType: 'outgoing',
        duration: 180,
        startTime: new Date('2024-12-10T14:30:00.000Z'),
        endTime: new Date('2024-12-10T14:33:00.000Z'),
        notes: 'Discussed warehouse position availability',
        createdBy: 'recruiter@company.com',
      },
    ]

    return {
      data: mockCallHistory,
      success: true,
      message: 'Call history retrieved successfully',
    }
  }

  async saveCallRecord(
    callData: Omit<CallRecord, '_id'>,
  ): Promise<CandidateApiResponse<CallRecord>> {
    await new Promise((resolve) => setTimeout(resolve, 700))

    const savedCall: CallRecord = {
      ...callData,
      _id: Date.now().toString(),
    }

    return {
      data: savedCall,
      success: true,
      message: 'Call record saved successfully',
    }
  }

  // Timesheets
  async getTimesheets(candidateId: string): Promise<CandidateApiResponse<TimesheetEntry[]>> {
    await new Promise((resolve) => setTimeout(resolve, 500))

    const mockTimesheets: TimesheetEntry[] = [
      {
        id: '1',
        employeeId: candidateId,
        weekEnding: new Date('2024-03-01'),
        regularHours: 40,
        overtimeHours: 8,
        doubletimeHours: 0,
        ptoHours: 0,
        status: 'approved',
      },
    ]

    return {
      data: mockTimesheets,
      success: true,
      message: 'Timesheets retrieved successfully',
    }
  }

  // Tax Setup
  async getTaxSetup(candidateId: string): Promise<CandidateApiResponse<TaxSetup[]>> {
    await new Promise((resolve) => setTimeout(resolve, 450))

    const mockTaxSetup: TaxSetup[] = [
      {
        id: '1',
        employeeId: candidateId,
        jurisdiction: 'Federal',
        filingStatus: 'Single',
        exemptions: 1,
        additionalWithholding: '$0.00',
        exempt: false,
      },
      {
        id: '2',
        employeeId: candidateId,
        jurisdiction: 'California',
        filingStatus: 'Single',
        exemptions: 1,
        additionalWithholding: '$0.00',
        exempt: false,
      },
    ]

    return {
      data: mockTaxSetup,
      success: true,
      message: 'Tax setup retrieved successfully',
    }
  }

  async updateTaxSetup(
    candidateId: string,
    taxSetupId: string,
    taxSetup: Partial<TaxSetup>,
  ): Promise<CandidateApiResponse<TaxSetup>> {
    await new Promise((resolve) => setTimeout(resolve, 800))

    const updated: TaxSetup = {
      id: taxSetupId,
      employeeId: candidateId,
      jurisdiction: 'Federal',
      filingStatus: 'Single',
      exemptions: 1,
      additionalWithholding: '$0.00',
      exempt: false,
      ...taxSetup,
    }

    return {
      data: updated,
      success: true,
      message: 'Tax setup updated successfully',
    }
  }

  async getPaychecks(
    year?: number,
    assignmentId?: string,
  ): Promise<CandidateApiResponse<CandidatePaychecksListData>> {
    try {
      const params: Record<string, string | number> = {}
      if (year !== undefined) params.year = year
      if (assignmentId) params.assignmentId = assignmentId
      const response = await this.client.get<CandidatePaychecksListData>(
        '/candidate-portal/paychecks',
        Object.keys(params).length > 0 ? {params} : undefined,
      )
      return {
        data: response.data,
        success: true,
        message: 'Paychecks retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching paychecks:', error)
      throw error
    }
  }

  async getDocuments(): Promise<CandidateApiResponse<CandidateDocumentsListData>> {
    try {
      const response = await this.client.get<CandidateDocumentsListData>(
        '/candidate-portal/documents',
      )
      return {
        data: response.data,
        success: true,
        message: 'Documents retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching documents:', error)
      throw error
    }
  }

  async downloadDocument(documentId: string, folderName: string, fileId: string): Promise<Blob> {
    try {
      const response = await this.client.get<Blob>('/candidate-portal/documents/download', {
        params: {documentId, folderName, fileId},
        responseType: 'blob',
      })
      return response.data instanceof Blob ? response.data : new Blob([response.data])
    } catch (error: unknown) {
      reportCatchError('Error downloading document:', error)
      throw error
    }
  }

  // Saved Filters
  async getSavedFilters(userId: string): Promise<CandidateApiResponse<SavedFilter[]>> {
    await new Promise((resolve) => setTimeout(resolve, 300))

    const mockSavedFilters: SavedFilter[] = [
      {
        id: '1',
        name: 'Available Warehouse Workers',
        filters: {
          employmentType: ['Full-time'],
          talentStatus: ['Active'],
          workHistorySearch: 'warehouse',
        },
        isGlobal: false,
        isDefault: true,
        createdBy: userId,
        createdAt: new Date('2024-01-15'),
      },
      {
        id: '2',
        name: 'Recently Active Candidates',
        filters: {
          talentStatus: ['Active'],
        },
        isGlobal: true,
        isDefault: false,
        createdBy: 'admin',
        createdAt: new Date('2024-02-01'),
      },
    ]

    return {
      data: mockSavedFilters,
      success: true,
      message: 'Saved filters retrieved successfully',
    }
  }

  async saveFilter(
    filter: Omit<SavedFilter, 'id' | 'createdAt'>,
  ): Promise<CandidateApiResponse<SavedFilter>> {
    await new Promise((resolve) => setTimeout(resolve, 500))

    const savedFilter: SavedFilter = {
      ...filter,
      id: Date.now().toString(),
      createdAt: new Date(),
    }

    return {
      data: savedFilter,
      success: true,
      message: 'Filter saved successfully',
    }
  }

  async deleteFilter(filterId: string): Promise<CandidateApiResponse<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400))

    return {
      data: undefined,
      success: true,
      message: 'Filter deleted successfully',
    }
  }

  /**
   * Terminate a candidate
   * POST /applicants/terminate/:applicantId
   */
  async terminateCandidate(
    applicantId: string,
    data: {
      terminationDate: string
      terminationType: string
      terminationReason: string
      comment?: string
    },
  ): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.post<Applicant>(
        `/applicants/terminate/${applicantId}`,
        data,
      )
      return {
        data: response.data,
        success: true,
        message: 'Employee terminated successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error terminating candidate:', error)
      throw error
    }
  }

  /**
   * Get termination info for a candidate
   * GET /applicants/terminationInfo/:applicantId
   */
  async getTerminationInfo(applicantId: string): Promise<{
    isTerminated: boolean
    terminationDate?: string
    terminationType?: string
    terminationReason?: string
    terminationComment?: string
    terminatedBy?: string
    terminatedAt?: string
  } | null> {
    try {
      const response = await this.client.get<{
        isTerminated?: boolean
        terminationDate?: string
        terminationType?: string
        terminationReason?: string
        terminationComment?: string
        terminatedBy?: string
        terminatedAt?: string
      }>(`/applicants/terminationInfo/${applicantId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching termination info:', error)
      throw error
    }
  }

  /**
   * Reactivate a terminated candidate
   * PUT /applicants/reactivate/:applicantId
   */
  async reactivateCandidate(applicantId: string): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.put<Applicant>(`/applicants/reactivate/${applicantId}`)
      return {
        data: response.data,
        success: true,
        message: 'Employee reactivated successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error reactivating candidate:', error)
      throw error
    }
  }

  /**
   * Submit compliance forms for an applicant
   * POST /applicants/submitComplianceForms
   */
  async submitComplianceForms(
    request: SubmitComplianceFormsRequest,
  ): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.post<Applicant>(
        '/applicants/submitComplianceForms',
        request,
      )
      return {
        data: response.data,
        success: true,
        message: 'Compliance forms submitted successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error submitting compliance forms:', error)
      throw error
    }
  }

  /**
   * Get applicant by ID
   * GET /applicant/getApplicant/:id
   */
  async getApplicantById(id: string): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.get<Applicant>(`/applicant/getApplicant/${id}`)
      return {
        data: response.data,
        success: true,
        message: 'Applicant fetched successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching applicant by ID:', error)
      throw error
    }
  }

  /**
   * Look up an applicant by full SSN.
   * POST /applicants/getApplicantBySSN
   *
   * SECURITY: The API response includes the decrypted full SSN in a `ssn` field.
   * This method destructures and discards it before returning so the full SSN
   * never enters component state. The caller receives only the safe fields
   * defined in ApplicantSSNLookupResult.
   *
   * NOTE on agencyId: the backend request DTO only declares `ssn`. The global
   * ValidationPipe (whitelist: true) silently strips `agencyId` from the body,
   * so the lookup is scoped by the caller's own accessible agencies, not by the
   * supplied agencyId. For platform admin that set is everything, so a 404 means
   * the SSN does not exist in the system. EOR-scoping must be validated on the
   * FE by comparing result.agencyId to the selected EOR.
   */
  async getApplicantBySSN(ssn: string, agencyId: string): Promise<ApplicantSSNLookupResult> {
    try {
      const response = await this.client.post<ApplicantSSNLookupResult & {ssn?: string}>(
        '/applicants/getApplicantBySSN',
        {ssn, agencyId},
      )
      // Destructure out the full SSN — it must not reach component state.
      const {ssn: _discard, ...safe} = response.data
      return safe
    } catch (error: unknown) {
      // Do not pass the raw Axios error — error.config.data contains the SSN
      // payload and would appear in the dev console via reportCatchError's
      // console.error. Extract only the message string and wrap it.
      const safeMsg = error instanceof Error ? error.message : 'SSN lookup failed'
      reportCatchError('Error looking up applicant by SSN:', new Error(safeMsg))
      // Still throw the original so the caller can read the HTTP status code.
      throw error
    }
  }

  /**
   * Update applicant
   * PUT /applicant/updateApplicant/:id
   */
  async updateApplicant(
    id: string,
    applicantData: Partial<Applicant>,
  ): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.put<Applicant>(
        `/applicants/updateApplicant`,
        applicantData,
      )
      return {
        data: response.data,
        success: true,
        message: 'Applicant updated successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error updating applicant:', error)
      throw error
    }
  }

  /**
   * Get applicants by service order
   * GET /applicant/getApplicantsByServiceOrder/:serviceOrderId
   */
  async getApplicantsByServiceOrder(
    serviceOrderId: string,
  ): Promise<CandidateApiResponse<Applicant[]>> {
    try {
      const response = await this.client.get<Applicant[]>(
        `/applicant/getApplicantsByServiceOrder/${serviceOrderId}`,
      )
      return {
        data: response.data,
        success: true,
        message: 'Applicants fetched successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching applicants by service order:', error)
      throw error
    }
  }

  async getProfile(): Promise<CandidateApiResponse<CandidateProfileData>> {
    try {
      const response = await this.client.get<CandidateProfileData>('/candidate-portal/profile')
      return {
        data: response.data,
        success: true,
        message: 'Profile retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate profile:', error)
      throw error
    }
  }

  async updateEmergencyContact(
    emergencyContact: EmergencyContact,
  ): Promise<CandidateApiResponse<EmergencyContact>> {
    try {
      const response = await this.client.put<EmergencyContact>(
        '/candidate-portal/profile/emergency-contact',
        emergencyContact,
      )
      return {
        data: response.data,
        success: true,
        message: 'Emergency contact updated successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error updating candidate emergency contact:', error)
      throw error
    }
  }

  async updateCandidateContactInfo(update: {
    primaryContact?: string
    secondaryContact?: string
  }): Promise<CandidateApiResponse<PersonalDetails>> {
    try {
      const response = await this.client.put<PersonalDetails>(
        '/candidate-portal/profile/contact-info',
        update,
      )
      return {
        data: response.data,
        success: true,
        message: 'Contact information updated successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error updating candidate contact info:', error)
      throw error
    }
  }

  async updatePersonalDetails(
    update: PersonalDetailsUpdate,
  ): Promise<CandidateApiResponse<PersonalDetails>> {
    try {
      const response = await this.client.put<PersonalDetails>(
        '/candidate-portal/profile/personal-details',
        update,
      )
      return {
        data: response.data,
        success: true,
        message: 'Personal details updated successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error updating candidate personal details:', error)
      throw error
    }
  }

  async getJobInfo(assignmentId?: string): Promise<CandidateApiResponse<CandidateJobData>> {
    try {
      const params = assignmentId ? {params: {assignmentId}} : undefined
      const response = await this.client.get<CandidateJobData>('/candidate-portal/job', params)
      return {
        data: response.data,
        success: true,
        message: 'Job information retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate job info:', error)
      throw error
    }
  }

  async getTaxInfo(): Promise<CandidateApiResponse<TaxInfoResponse>> {
    try {
      const response = await this.client.get<TaxInfoResponse>('/candidate-portal/tax')
      return {
        data: response.data,
        success: true,
        message: 'Tax information retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate tax info:', error)
      throw error
    }
  }

  async getBankAccounts(): Promise<CandidateApiResponse<BankAccount[]>> {
    try {
      const response = await this.client.get<BankAccount[]>('/candidate-portal/bank-accounts')
      return {
        data: response.data,
        success: true,
        message: 'Bank accounts retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate bank accounts:', error)
      throw error
    }
  }

  async getPortalTimecards(filters: {
    assignmentId?: string
    weekStartDate?: string
    weekEndDate?: string
  }): Promise<CandidateApiResponse<TimecardResponse[]>> {
    try {
      const params = new URLSearchParams()
      if (filters.assignmentId) params.append('assignmentId', filters.assignmentId)
      if (filters.weekStartDate) params.append('weekStartDate', filters.weekStartDate)
      if (filters.weekEndDate) params.append('weekEndDate', filters.weekEndDate)

      const response = await this.client.get<TimecardResponse[]>(
        `/candidate-portal/timesheets${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return {
        data: response.data,
        success: true,
        message: 'Timesheets retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate timesheets:', error)
      throw error
    }
  }

  async getCandidateAssignments(): Promise<CandidateApiResponse<CandidateAssignment[]>> {
    try {
      const response = await this.client.get<CandidateAssignment[]>('/candidate-portal/assignments')
      return {
        data: response.data,
        success: true,
        message: 'Assignments retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate assignments:', error)
      return {
        data: [],
        success: false,
        message: 'Failed to fetch assignments',
      }
    }
  }

  async getDashboardData(assignmentId?: string): Promise<CandidateApiResponse<DashboardData>> {
    try {
      const params = assignmentId ? {params: {assignmentId}} : undefined
      const response = await this.client.get<DashboardData>('/candidate-portal/dashboard', params)
      return {
        data: response.data,
        success: true,
        message: 'Dashboard data retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate dashboard:', error)
      return {
        data: {
          nextPaycheck: null,
          pendingDocuments: null,
          currentAssignment: null,
          recentActivity: [],
          announcements: [],
          contacts: {hrContact: null, safetyCoordinatorContact: null},
          timeOffSummary: null,
        },
        success: false,
        message: 'Failed to fetch dashboard data',
      }
    }
  }

  async getTimeOffSummary(
    assignmentId: string,
  ): Promise<CandidateApiResponse<CandidateTimeOffSummary>> {
    try {
      const response = await this.client.get<CandidateTimeOffSummaryApiResponse>(
        '/candidate-portal/time-off/summary',
        {params: {assignmentId}},
      )
      const policies = response.data.balances
        .filter((balance) => !!balance.policyId)
        .map((balance) => ({
          policyId: balance.policyId ?? '',
          categoryId: balance.categoryId ?? '',
          policyName: balance.policyName,
          categoryName: balance.typeName,
          accent: balance.accent,
          kind: balance.availableHours === null ? ('unpaid' as const) : ('tracked' as const),
          availableHours: balance.availableHours,
          usedHours: balance.usedHours,
          totalHours:
            balance.availableHours === null ? null : balance.availableHours + balance.usedHours,
          progressPercent: balance.usagePercent,
          accrualDisplay: balance.accrualLines.join(' · '),
          accrualLines: balance.accrualLines,
          capDisplay:
            balance.accrualLines.find((line) => line.toLowerCase().includes('cap')) ?? null,
          nextAccrualDate: balance.nextAccrual,
          annualUsageLimitHours: balance.annualUsageLimitHours,
          annualUsedHours: balance.annualUsedHours,
          annualPendingHours: balance.annualPendingHours,
          annualRemainingHours: balance.annualRemainingHours,
        }))
      return {
        data: {
          policies,
          history: response.data.history ?? [],
          pendingRequestsCount: response.data.pendingRequestsCount ?? 0,
          // STFFLG-4310: an msp before it omits the field; read that as unknown.
          hireDate: response.data.hireDate ?? null,
        },
        success: true,
        message: 'Time off summary retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate time off summary:', error)
      throw error
    }
  }

  async getTimeOffRequests(
    assignmentId: string,
  ): Promise<CandidateApiResponse<CandidateTimeOffRequest[]>> {
    try {
      const response = await this.client.get<CandidateTimeOffRequestApiResponse[]>(
        '/candidate-portal/time-off/requests',
        {params: {assignmentId}},
      )
      return {
        data: (response.data ?? []).map(mapCandidateTimeOffRequest),
        success: true,
        message: 'Time off requests retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate time off requests:', error)
      throw error
    }
  }

  async getTimeOffRequestById(
    requestId: string,
  ): Promise<CandidateApiResponse<CandidateTimeOffRequest>> {
    try {
      const response = await this.client.get<CandidateTimeOffRequestApiResponse>(
        `/candidate-portal/time-off/requests/${requestId}`,
      )
      return {
        data: mapCandidateTimeOffRequest(response.data),
        success: true,
        message: 'Time off request retrieved successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching candidate time off request:', error)
      throw error
    }
  }

  async createTimeOffRequest(
    input: CandidateCreateTimeOffRequestInput,
  ): Promise<CandidateApiResponse<CandidateTimeOffRequest>> {
    try {
      const response = await this.client.post<CandidateTimeOffRequestApiResponse>(
        '/candidate-portal/time-off/requests',
        input,
      )
      return {
        data: mapCandidateTimeOffRequest(response.data),
        success: true,
        message: 'Time off request created successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error creating candidate time off request:', error)
      throw error
    }
  }

  async cancelTimeOffRequest(
    requestId: string,
  ): Promise<CandidateApiResponse<CandidateTimeOffRequest>> {
    try {
      const response = await this.client.post<CandidateTimeOffRequestApiResponse>(
        `/candidate-portal/time-off/requests/${requestId}/cancel`,
      )
      return {
        data: mapCandidateTimeOffRequest(response.data),
        success: true,
        message: 'Time off request cancelled successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error cancelling candidate time off request:', error)
      throw error
    }
  }

  async projectTimeOffBalance(
    input: CandidateTimeOffProjectionInput,
  ): Promise<CandidateApiResponse<CandidateTimeOffProjection>> {
    try {
      const response = await this.client.post<CandidateTimeOffProjection>(
        '/candidate-portal/time-off/project',
        input,
      )
      return {
        data: response.data,
        success: true,
        message: 'Time off balance projected successfully',
      }
    } catch (error: unknown) {
      reportCatchError('Error projecting candidate time off balance:', error)
      throw error
    }
  }
}

export const candidateApi = new CandidateApiService()
